import { QuotationMode, QuotationType, ModeMilestone } from '@library/common/models';
import { BillingParty, Reference, Status } from '@library/common';
import { IGenericType } from '@library/common/generictype';

export interface IQuotation {
    fwq: string;
    references: Reference[];
    serviceLevel: string;
    statuses: Status[];
    milestones?: IGenericType;
    shipper: BillingParty;
    consignee: BillingParty;
    bill_to: BillingParty;
    mode: QuotationMode;
    quoteType: QuotationType;
    cost: string;
    createdDate: string;
    createdTime: string;
    cancelled?: boolean;
}

export interface IQuotationSnapshot extends IQuotation {
    toString: () => string;
}

export class QuotationSnapshot implements IQuotationSnapshot {
    fwq: string;
    references: Reference[];
    bill_to: BillingParty;
    consignee: BillingParty;
    milestones?: IGenericType;
    serviceLevel: string;
    shipper: BillingParty;
    statuses: Status[];
    mode: QuotationMode;
    quoteType: QuotationType;
    cost: string;
    costCurrency: string;
    createdDate: string;
    createdTime: string;
    validToDate: string;
    cancelled: boolean;

    datetime: number;

    constructor( quote?: IGenericType<IQuotation> ) {
        this.fwq = quote?.fwq;
        this.serviceLevel = quote?.serviceLevel;
        this.mode = quote?.mode || 'road';
        this.quoteType = quote?.quoteType || 'domestic';
        this.references = [];
        if ( quote?.references ) {
            this.references = quote.references.map( ref => new Reference( ref ));
        }

        this.statuses = (quote?.statuses || []).map( status => new Status( status ));

        this.milestones = new ModeMilestone( quote?.milestones );

        this.shipper = new BillingParty( quote?.shipper );
        this.consignee = new BillingParty( quote?.consignee );
        this.bill_to = new BillingParty( quote?.bill_to );
        this.cost = quote?.cost || '0.000000';
        this.costCurrency = quote?.costCurrency || 'USD';

        const currentDate = new Date();

        const date = quote?.createdDate || currentDate.getUTCFullYear() + '-' +
            (currentDate.getUTCMonth() + 1).toString().padStart(2, '0') + '-' + currentDate.getDate().toString().padStart(2, '0');
        const time = quote?.createdTime || currentDate.getUTCHours().toString().padStart(2, '0') + ':' + currentDate.getUTCMinutes().toString().padStart(2, '0') + ':' + currentDate.getUTCSeconds().toString().padStart(2, '0');

        if ( date.includes('-') ) {
            // date is still in UTC
            // do not allow non-UTC date time strings to be converted
            const datetime = date + 'T' + time + '.000Z';

            this.datetime = (new Date( datetime )).getTime();

            this.createdDate = new Date( datetime ).toLocaleDateString().split('/').map((i) => i.padStart(2, '0')).join('/');
            this.createdTime = new Date( datetime ).toTimeString().split(' ')[0];
        }

        this.validToDate = quote?.validToDate;
        this.cancelled = quote?.cancelled === true;
    }

    toString() {
        return [
            this.fwq,
            this.references.map( r => r.toString()).join('◬'),
            this.statuses.map( s => s.toString()).join('◬'),
            (this.shipper) ? this.shipper.toString() : '',
            (this.consignee) ? this.consignee.toString() : '',
            (this.bill_to) ? this.bill_to.toString() : '',
            this.cost?.toString || '',
         ].join('◬').toLowerCase();
    }
}

export class DefaultQuotationSnapshot extends QuotationSnapshot {
    constructor() {
        super();

        this.fwq = '0000000000';
        this.references = [];
        this.serviceLevel = 'Quotation';
        this.statuses = [];
        this.milestones = {};
        this.mode = 'road';
        this.quoteType = 'domestic';
        this.cost = '0.000000';

        this.shipper = {
            id: -1,
            name: 'Shipper Name',
            address1: '123 Street St',
            city: 'Anytown',
            state: 'Anywhere',
            country: 'US',
        };

        this.consignee = {
            id: -1,
            name: 'Consignee Name',
            address1: '123 Brick Rd',
            city: 'Somewhere',
            state: 'Over the rainbow',
            country: 'US',
        };
    }
}
